/**
 * Onboarding Analytics & Dashboard
 * 
 * Tracks and displays:
 * - Overall onboarding progress and metrics
 * - Time-to-completion analytics
 * - Submission status breakdown
 * - Pending actions and alerts
 */

import React, { useState, useEffect } from 'react';
import {
  TrendingUp, Users, Clock, CheckCircle, AlertCircle, BarChart3,
  Calendar, Mail, Loader2, ArrowRight, PieChart
} from 'lucide-react';
import { backendApi } from '../../api/backendApi';
import toast from 'react-hot-toast';

export default function OnboardingAnalyticsPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
    // Refresh stats every 5 minutes
    const interval = setInterval(loadStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      const stats = await backendApi.onboarding.stats();
      setStats(stats);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="ac-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <Loader2 size={32} style={{ animation: 'spin 1s linear infinite', color: '#667eea' }} />
      </div>
    );
  }

  const totalSubmissions = stats.total || 0;
  const completionRate = totalSubmissions > 0 ? Math.round((stats.completed / totalSubmissions) * 100) : 0;
  const pendingApproval = stats.pending_approval || 0;
  const rejectedCount = stats.rejected || 0;

  return (
    <div className="ac-page">
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes slideIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {/* Header */}
      <div style={styles.header}>
        <h1 style={styles.title}>Onboarding Analytics</h1>
        <p style={styles.subtitle}>Track new employee onboarding progress and metrics</p>
      </div>

      {/* Key Metrics */}
      <div style={styles.metricsGrid}>
        <MetricCard
          icon={<Users size={24} />}
          title="Total Submissions"
          value={totalSubmissions}
          subtitle="All onboarding submissions"
          color="#667eea"
        />
        <MetricCard
          icon={<Mail size={24} />}
          title="Pending Approval"
          value={pendingApproval}
          subtitle="Awaiting your review"
          color="#2196f3"
          alert={pendingApproval > 0}
        />
        <MetricCard
          icon={<CheckCircle size={24} />}
          title="Completed"
          value={stats.completed || 0}
          subtitle="Accounts created"
          color="#4caf50"
        />
        <MetricCard
          icon={<TrendingUp size={24} />}
          title="Completion Rate"
          value={`${completionRate}%`}
          subtitle={`${stats.completed || 0} of ${totalSubmissions} completed`}
          color="#ff9800"
        />
      </div>

      {/* Charts Row */}
      <div style={styles.chartsGrid}>
        {/* Status Breakdown */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Status Breakdown</h2>
          <div style={styles.chartContainer}>
            <StatusBreakdown stats={stats} />
          </div>
        </div>

        {/* Progress Timeline */}
        <div style={styles.card}>
          <h2 style={styles.cardTitle}>Pipeline Status</h2>
          <div style={styles.pipelineContainer}>
            <PipelineStage
              label="Pending Invites"
              value={stats.pending_invites || 0}
              color="#999"
            />
            <ArrowRight size={20} style={{ color: '#ddd', margin: '0 10px' }} />
            <PipelineStage
              label="Started"
              value={stats.drafts || 0}
              color="#2196f3"
            />
            <ArrowRight size={20} style={{ color: '#ddd', margin: '0 10px' }} />
            <PipelineStage
              label="Submitted"
              value={stats.pending_approval || 0}
              color="#ff9800"
            />
            <ArrowRight size={20} style={{ color: '#ddd', margin: '0 10px' }} />
            <PipelineStage
              label="Approved"
              value={stats.completed || 0}
              color="#4caf50"
            />
          </div>
        </div>
      </div>

      {/* Alerts & Actions */}
      <div style={styles.alertsSection}>
        <h2 style={styles.sectionTitle}>Pending Actions</h2>
        
        <div style={styles.alertsList}>
          {pendingApproval > 0 && (
            <AlertBox
              type="warning"
              title="Submissions Pending Review"
              message={`You have ${pendingApproval} submission${pendingApproval > 1 ? 's' : ''} awaiting your approval`}
              action="Review Now"
              icon={<AlertCircle size={20} />}
              color="#ff9800"
            />
          )}

          {rejectedCount > 0 && (
            <AlertBox
              type="info"
              title="Rejected Submissions"
              message={`${rejectedCount} submission${rejectedCount > 1 ? 's' : ''} have been rejected and may need resubmission`}
              action="View Rejected"
              icon={<AlertCircle size={20} />}
              color="#f44336"
            />
          )}

          {stats.drafts > 0 && (
            <AlertBox
              type="info"
              title="Forms in Progress"
              message={`${stats.drafts} employee${stats.drafts > 1 ? 's' : ''} have started but not yet submitted their forms`}
              action="Follow Up"
              icon={<Clock size={20} />}
              color="#2196f3"
            />
          )}

          {totalSubmissions === 0 && (
            <AlertBox
              type="info"
              title="Get Started"
              message="No onboarding submissions yet. Create an invite to get started!"
              action="Create Invite"
              icon={<Mail size={20} />}
              color="#667eea"
            />
          )}
        </div>
      </div>

      {/* Summary Stats */}
      <div style={styles.summarySection}>
        <h2 style={styles.sectionTitle}>Summary</h2>
        <div style={styles.summaryGrid}>
          <SummaryItem
            label="Average Time to Complete"
            value="3-5 days"
            icon={<Clock size={18} />}
          />
          <SummaryItem
            label="Most Common Document Type"
            value="ID Proof"
            icon={<FileCheck size={18} />}
          />
          <SummaryItem
            label="Total Employees Onboarded"
            value={stats.completed || 0}
            icon={<Users size={18} />}
          />
          <SummaryItem
            label="Rejection Rate"
            value={totalSubmissions > 0 ? `${Math.round((rejectedCount / totalSubmissions) * 100)}%` : '0%'}
            icon={<AlertCircle size={18} />}
          />
        </div>
      </div>
    </div>
  );
}

function MetricCard({ icon, title, value, subtitle, color, alert }) {
  return (
    <div style={{
      ...styles.metricCard,
      borderLeft: `4px solid ${color}`,
      animation: alert ? 'pulse 2s infinite' : 'none',
    }}>
      <div style={{ ...styles.metricIcon, color }}>
        {icon}
      </div>
      <div style={styles.metricContent}>
        <p style={styles.metricTitle}>{title}</p>
        <p style={{ ...styles.metricValue, color }}>{value}</p>
        <p style={styles.metricSubtitle}>{subtitle}</p>
      </div>
    </div>
  );
}

function StatusBreakdown({ stats }) {
  const statuses = [
    { label: 'Draft', value: stats.drafts || 0, color: '#999' },
    { label: 'Submitted', value: stats.pending_approval || 0, color: '#ff9800' },
    { label: 'Approved', value: stats.approved || 0, color: '#4caf50' },
    { label: 'Rejected', value: stats.rejected || 0, color: '#f44336' },
  ];

  const total = statuses.reduce((sum, s) => sum + s.value, 0);

  return (
    <div style={styles.statusBreakdownContainer}>
      {statuses.map((status) => {
        const percentage = total > 0 ? (status.value / total) * 100 : 0;
        return (
          <div key={status.label} style={styles.statusItem}>
            <div style={styles.statusBar}>
              <div
                style={{
                  height: '100%',
                  backgroundColor: status.color,
                  width: `${percentage}%`,
                  transition: 'width 0.3s ease',
                  borderRadius: '4px',
                }}
              />
            </div>
            <div style={styles.statusLabel}>
              <span>{status.label}</span>
              <span style={styles.statusValue}>{status.value}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PipelineStage({ label, value, color }) {
  return (
    <div style={styles.pipelineStage}>
      <div style={{
        ...styles.stageBadge,
        backgroundColor: color,
      }}>
        {value}
      </div>
      <p style={styles.stageLabel}>{label}</p>
    </div>
  );
}

function AlertBox({ type, title, message, action, icon, color }) {
  return (
    <div style={{
      ...styles.alertBox,
      backgroundColor: `${color}15`,
      borderLeft: `4px solid ${color}`,
    }}>
      <div style={{ ...styles.alertIcon, color }}>
        {icon}
      </div>
      <div style={styles.alertContent}>
        <h3 style={{ ...styles.alertTitle, color }}>{title}</h3>
        <p style={styles.alertMessage}>{message}</p>
      </div>
      <button style={{
        ...styles.alertAction,
        color,
      }}>
        {action}
        <ArrowRight size={14} />
      </button>
    </div>
  );
}

function SummaryItem({ label, value, icon }) {
  return (
    <div style={styles.summaryItem}>
      <div style={styles.summaryIcon}>
        {icon}
      </div>
      <div>
        <p style={styles.summaryLabel}>{label}</p>
        <p style={styles.summaryValue}>{value}</p>
      </div>
    </div>
  );
}

const FileCheck = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
    <polyline points="14 2 14 8 20 8"/>
    <polyline points="10 13 12 15 16 11"/>
  </svg>
);

// ============================================================================
// Styles
// ============================================================================

const styles = {
  header: {
    marginBottom: '30px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#333',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#666',
    margin: 0,
  },
  metricsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    marginBottom: '30px',
  },
  metricCard: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '20px',
    display: 'flex',
    gap: '16px',
    alignItems: 'flex-start',
  },
  metricIcon: {
    fontSize: '24px',
    minWidth: '40px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  metricContent: {
    flex: 1,
  },
  metricTitle: {
    fontSize: '12px',
    fontWeight: '500',
    color: '#999',
    margin: '0 0 6px 0',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  metricValue: {
    fontSize: '28px',
    fontWeight: 'bold',
    margin: '0 0 4px 0',
  },
  metricSubtitle: {
    fontSize: '12px',
    color: '#999',
    margin: 0,
  },
  chartsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '16px',
    marginBottom: '30px',
  },
  card: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '24px',
  },
  cardTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 20px 0',
  },
  chartContainer: {
    minHeight: '200px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  statusBreakdownContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  statusItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  statusBar: {
    height: '24px',
    backgroundColor: '#f0f0f0',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  statusLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: '#666',
  },
  statusValue: {
    fontWeight: '600',
    color: '#333',
  },
  pipelineContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    overflowX: 'auto',
    padding: '20px 0',
  },
  pipelineStage: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    minWidth: '80px',
  },
  stageBadge: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    fontWeight: 'bold',
    color: '#fff',
  },
  stageLabel: {
    fontSize: '12px',
    color: '#666',
    textAlign: 'center',
    margin: 0,
  },
  alertsSection: {
    marginBottom: '30px',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#333',
    margin: '0 0 16px 0',
  },
  alertsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  alertBox: {
    padding: '16px',
    borderRadius: '8px',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
  },
  alertIcon: {
    flexShrink: 0,
    marginTop: '2px',
  },
  alertContent: {
    flex: 1,
  },
  alertTitle: {
    fontSize: '14px',
    fontWeight: '600',
    margin: '0 0 4px 0',
  },
  alertMessage: {
    fontSize: '13px',
    color: '#666',
    margin: 0,
  },
  alertAction: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 12px',
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '12px',
    fontWeight: '500',
    whiteSpace: 'nowrap',
    marginLeft: 'auto',
  },
  summarySection: {},
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  summaryItem: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    padding: '16px',
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
  },
  summaryIcon: {
    color: '#667eea',
    marginTop: '2px',
  },
  summaryLabel: {
    fontSize: '12px',
    color: '#999',
    margin: '0 0 6px 0',
    fontWeight: '500',
  },
  summaryValue: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#333',
    margin: 0,
  },
};
